import React from "react";
import SEO from "../components/SEO";
import { useContent } from "../lib/content";
import { buildWhatsAppLink } from "../lib/api";
import { Phone, Mail, MapPin, Instagram, MessageCircle, Facebook } from "lucide-react";

export default function ContactPage() {
  const { settings, contact } = useContent();

  return (
    <>
      <SEO title="تماس با ما | تحسین" description={contact.intro?.slice(0, 160)} />
      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-14 text-right" data-testid="contact-page">
        {contact.eyebrow && (
          <span className="text-amber-700 font-semibold text-sm tracking-widest uppercase">{contact.eyebrow}</span>
        )}
        <h1 className="text-4xl sm:text-5xl font-black text-stone-900 mt-3 mb-4">{contact.title}</h1>
        <p className="text-lg text-stone-600 leading-relaxed mb-10 max-w-2xl">{contact.intro}</p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <a
            href={buildWhatsAppLink(settings.whatsapp_number, "سلام، درباره محصولات تحسین سوال داشتم.")}
            target="_blank"
            rel="noreferrer"
            className="bg-white border border-stone-200 rounded-3xl p-6 product-card-hover group"
            data-testid="contact-whatsapp-card"
          >
            <div className="w-12 h-12 rounded-2xl bg-[#25D366] flex items-center justify-center mb-4">
              <MessageCircle className="w-6 h-6 text-white" />
            </div>
            <h3 className="text-lg font-bold text-stone-900 mb-1">واتساپ</h3>
            <p className="text-stone-600 persian-numerals">{settings.contact_phone}</p>
            {contact.whatsapp_subtitle && (
              <p className="text-xs text-stone-500 mt-1">{contact.whatsapp_subtitle}</p>
            )}
            <span className="mt-3 inline-block text-sm text-amber-700 group-hover:translate-x-1 transition-transform">
              شروع گفتگو ←
            </span>
          </a>

          {settings.instagram_url && (
            <a
              href={settings.instagram_url}
              target="_blank"
              rel="noreferrer"
              className="bg-white border border-stone-200 rounded-3xl p-6 product-card-hover group"
              data-testid="contact-instagram-card"
            >
              <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-fuchsia-500 to-amber-500 flex items-center justify-center mb-4">
                <Instagram className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-lg font-bold text-stone-900 mb-1">اینستاگرام</h3>
              <p className="text-stone-600">@tahsin.honey</p>
              {contact.instagram_subtitle && (
                <p className="text-xs text-stone-500 mt-1">{contact.instagram_subtitle}</p>
              )}
              <span className="mt-3 inline-block text-sm text-amber-700 group-hover:translate-x-1 transition-transform">
                دنبال کنید ←
              </span>
            </a>
          )}

          {settings.facebook_url && (
            <a
              href={settings.facebook_url}
              target="_blank"
              rel="noreferrer"
              className="bg-white border border-stone-200 rounded-3xl p-6 product-card-hover group"
              data-testid="contact-facebook-card"
            >
              <div className="w-12 h-12 rounded-2xl bg-[#1877F2] flex items-center justify-center mb-4">
                <Facebook className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-lg font-bold text-stone-900 mb-1">فیسبوک</h3>
              <p className="text-stone-600">{contact.facebook_subtitle || "صفحه رسمی تحسین"}</p>
              {settings.contact_phone_2 && (
                <p className="text-sm text-stone-500 mt-1 persian-numerals">
                  شماره ثبت‌شده: {settings.contact_phone_2}
                </p>
              )}
            </a>
          )}

          {settings.contact_email && (
            <a
              href={`mailto:${settings.contact_email}`}
              className="bg-white border border-stone-200 rounded-3xl p-6 product-card-hover group"
              data-testid="contact-email-card"
            >
              <div className="w-12 h-12 rounded-2xl bg-stone-800 flex items-center justify-center mb-4">
                <Mail className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-lg font-bold text-stone-900 mb-1">ایمیل</h3>
              <p className="text-stone-600">{settings.contact_email}</p>
              {contact.email_subtitle && (
                <p className="text-xs text-stone-500 mt-1 block">{contact.email_subtitle}</p>
              )}
            </a>
          )}

          {settings.contact_phone && (
            <a
              href={`tel:${settings.contact_phone.replace(/\s/g, "")}`}
              className="bg-white border border-stone-200 rounded-3xl p-6 product-card-hover group"
              data-testid="contact-phone-card"
            >
              <div className="w-12 h-12 rounded-2xl honey-gradient flex items-center justify-center mb-4">
                <Phone className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-lg font-bold text-stone-900 mb-1">تلفن</h3>
              <p className="text-stone-600 persian-numerals">{settings.contact_phone}</p>
              {settings.contact_phone_2 && (
                <p className="text-sm text-stone-500 mt-1 persian-numerals">{settings.contact_phone_2}</p>
              )}
              {contact.phone_subtitle && (
                <p className="text-xs text-stone-500 mt-1">{contact.phone_subtitle}</p>
              )}
            </a>
          )}

          <div className="bg-white border border-stone-200 rounded-3xl p-6">
            <div className="w-12 h-12 rounded-2xl bg-amber-100 flex items-center justify-center mb-4">
              <MapPin className="w-6 h-6 text-amber-700" />
            </div>
            <h3 className="text-lg font-bold text-stone-900 mb-1">آدرس</h3>
            <p className="text-stone-600">{settings.address}</p>
            {contact.address_subtitle && (
              <p className="text-xs text-stone-500 mt-1">{contact.address_subtitle}</p>
            )}
          </div>
        </div>
      </section>
    </>
  );
}
